import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { useCallback, useEffect, useState } from 'react';

import { Job, JobInvoicing } from '~hooks/useJob/models';

import ActionsColumn from './Columns/ActionsColumn';
import CheckboxColumn from './Columns/CheckboxColumn';
import DriverAndTruckColumn from './Columns/DriverAndTruckColumn';
import InvoiceColumn from './Columns/InvoiceColumn';
import LoadsColumn from './Columns/LoadsColumn';
import MarginColumn from './Columns/MarginColumn';
import RequestedEquipmentTypeColumn from './Columns/RequestedEquipmentTypeColum';
import StatusColumn from './Columns/StatusColumn';
import TimeAndDurationColumn from './Columns/TimeAndDurationColumn';
import TotalQuantityColumn from './Columns/TotalQuantityColumn';
import ExpandedBlock from './ExpandedBlock/ExpandedBlock';
import { columns, gridTemplateColumns } from './jobs-table-columns';

const rowGridColumn = `span ${columns.length}`;

interface TableRowProps {
  job: JobInvoicing;
  onViewJobCardPdf: (job: Job) => Promise<void>;
  isGeneratingJobCardPdf: boolean;
}

export default function TableRow({
  job: jobProp,
  onViewJobCardPdf,
  isGeneratingJobCardPdf,
}: TableRowProps) {
  const [job, setJob] = useState(jobProp);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((expanded) => !expanded);
  }, []);

  const handleJobUpdated = useCallback(
    (job: Job) => {
      const jobInvoicing = JobInvoicing.fromJobOrJobInvoicingAndInvoices(
        job,
        jobProp.receivableInvoice,
        jobProp.payableInvoice,
      );

      setJob(jobInvoicing);
    },
    [jobProp.receivableInvoice, jobProp.payableInvoice],
  );

  useEffect(() => {
    setJob(jobProp);
  }, [jobProp]);

  return (
    <>
      <Box
        data-test-id="table-row"
        onClick={toggleExpanded}
        sx={(theme) => ({
          borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          display: 'grid',
          gridAutoRows: '95px',
          gridColumn: rowGridColumn,
          gridTemplateColumns,
          overflow: 'hidden',
        })}
      >
        <CheckboxColumn sx={{ ...columns[0].sx }} job={job} />
        <DriverAndTruckColumn sx={{ ...columns[1].sx }} job={job} />
        <RequestedEquipmentTypeColumn sx={{ ...columns[2].sx }} job={job} />
        <StatusColumn sx={{ ...columns[3].sx }} job={job} />
        <TimeAndDurationColumn sx={{ ...columns[4].sx }} job={job} />
        <LoadsColumn sx={{ ...columns[5].sx }} job={job} />
        <TotalQuantityColumn sx={{ ...columns[6].sx }} job={job} />
        <InvoiceColumn job={job} invoiceType="receivable" sx={{ ...columns[7].sx }} />
        <InvoiceColumn job={job} invoiceType="payable" sx={{ ...columns[8].sx }} />
        <MarginColumn sx={{ ...columns[9].sx }} job={job} />
        <ActionsColumn
          sx={{ ...columns[10].sx }}
          job={job}
          onViewJobCardPdf={onViewJobCardPdf}
          isGeneratingJobCardPdf={isGeneratingJobCardPdf}
        />
      </Box>

      <Collapse
        in={isExpanded}
        mountOnEnter
        unmountOnExit
        sx={{ gridColumn: rowGridColumn }}
      >
        <Box
          sx={(theme) => ({
            borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          })}
        >
          <ExpandedBlock job={job} onJobUpdated={handleJobUpdated} />
        </Box>
      </Collapse>
    </>
  );
}
